import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { getLoanETA } from 'app/types/loan';
import { type Loan, LoanETA, LoanStatus } from 'app/types/loan';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { localeAwareFormat } from 'app/lib/date';

import { useUser } from 'app/network/user';
import { LoanReturnConfirmation, useLoanReturnConfirmation } from 'app/components/loan-return-confirmation';
import {
  CreateOfferFromRequestModal,
  useCreateOfferFromRequestModal,
} from 'app/components/create-offer-from-request-modal';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface ComponentProps {
  loan: Loan;
  loanETA?: LoanETA;
}

const loanStatusColors = {
  red: {
    background: 'surface.brand.secondary-4.default',
    foreground: 'text.primary',
  },
  yellow: {
    background: 'surface.brand.secondary-2.default',
    foreground: 'text.primary',
  },
  grey: {
    background: 'surface.inverse.tertiary-neutral',
    foreground: 'text.primary',
  },
} as const;

const getLoanManagementState = (loan: Loan, requestedLoanETA: LoanETA | undefined, userId: string | undefined) => {
  const realLoanETA = getLoanETA(loan);
  const loanETA = requestedLoanETA ?? realLoanETA;
  const isLender = loan.lenderId === userId;
  const otherUser = isLender ? loan.borrower : loan.lender;
  const isSell = loan.listing.transactionMode === TransactionMode.sell;
  const saleIsDue = realLoanETA === LoanETA.late || realLoanETA === LoanETA.ongoing;
  const saleAwaitingSeller = realLoanETA === LoanETA.returned;
  const coloredTitle = loanETA === LoanETA.late;
  const showBody = isSell
    ? saleIsDue || saleAwaitingSeller
    : realLoanETA === LoanETA.late || (realLoanETA === LoanETA.returned && loanETA === LoanETA.returned);
  const showCTA = isSell
    ? (saleIsDue && !saleAwaitingSeller) || (saleAwaitingSeller && isLender)
    : realLoanETA === LoanETA.late || (realLoanETA === LoanETA.returned && isLender);
  const disableCTA = isSell
    ? !showCTA
    : !(realLoanETA === LoanETA.late || (realLoanETA === LoanETA.returned && isLender));
  const loanStatusColor = loanETA === LoanETA.late ? 'red' : realLoanETA === LoanETA.returned ? 'yellow' : 'grey';
  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const tPrefix = isSell
    ? 'saleManagement'
    : isService
      ? 'serviceManagement'
      : isDonation
        ? 'donationManagement'
        : 'loanManagement';

  return {
    coloredTitle,
    disableCTA,
    isDonation,
    isLender,
    isSell,
    isService,
    loan,
    loanETA,
    loanStatusColorTokens: loanStatusColors[loanStatusColor],
    otherUser,
    realLoanETA,
    showBody,
    showCTA,
    showStatus: !coloredTitle,
    tPrefix,
  };
};

type LoanManagementState = ReturnType<typeof getLoanManagementState>;

const getManagementTitleKey = ({ isLender, loanETA, realLoanETA, tPrefix }: LoanManagementState) => {
  const eta = loanETA === LoanETA.late ? LoanETA.late : realLoanETA;
  const audience =
    realLoanETA === LoanETA.returned && loanETA !== LoanETA.late ? (isLender ? '.lender' : '.borrower') : '';
  return `${tPrefix}.title.${eta}${audience}`;
};

const getManagementStatusKey = ({ isDonation, isSell, isService, loanETA, realLoanETA }: LoanManagementState) => {
  if (loanETA === LoanETA.late) return LoanETA.late;
  if (isSell && realLoanETA === LoanETA.returned) return 'returnedSale';
  if (isService && realLoanETA === LoanETA.returned) return 'returnedService';
  if (isDonation && realLoanETA === LoanETA.returned) return 'returnedDonation';
  return realLoanETA;
};

const getLoanPeriodText = (loan: Loan, t: TFunction) => {
  if (loan.start === loan.end) {
    return `${t('loanMessage.date')} : ${localeAwareFormat(new Date(loan.start), 'd MMM')}`;
  }
  return `${t('loanMessage.period')} : ${localeAwareFormat(new Date(loan.start), 'd MMM')} ${t(
    'loanMessage.to',
  )} ${localeAwareFormat(new Date(loan.end), 'd MMM')}`;
};

const getManagementCtaKey = ({ isLender, isSell, loanETA, tPrefix }: LoanManagementState) =>
  `${tPrefix}.cta.${loanETA}${isSell ? `.${isLender ? 'lender' : 'borrower'}` : ''}`;

const LoanManagementCard = ({
  onConfirmReturn,
  state,
  t,
}: {
  onConfirmReturn: () => void;
  state: LoanManagementState;
  t: TFunction;
}) => {
  const {
    coloredTitle,
    disableCTA,
    isLender,
    loan,
    loanETA,
    loanStatusColorTokens,
    otherUser,
    showBody,
    showCTA,
    showStatus,
    tPrefix,
  } = state;

  return (
    <Column gap="spacing.general.x4" style={styles.block} width="100%">
      <View
        style={{
          borderRadius: theme.spacing['spacing.general.x1'],
          paddingVertical: coloredTitle ? 4 : 0,
          paddingHorizontal: coloredTitle ? 10 : 0,
          alignSelf: 'flex-start',
          backgroundColor: coloredTitle ? theme.colors[loanStatusColorTokens.background] : 'transparent',
        }}
      >
        <Text
          color={coloredTitle ? loanStatusColorTokens.foreground : 'text.primary'}
          fontWeight="700"
          variant="bodyBase"
        >
          {t(getManagementTitleKey(state), { name: otherUser.profile!.firstName })}
        </Text>
      </View>
      <Text variant="bodyBase">{getLoanPeriodText(loan, t)}</Text>
      {showStatus ? (
        <Row alignItems="center">
          <Text variant="bodyBase">{t(`${tPrefix}.status`)} : </Text>
          <View
            style={{
              borderRadius: theme.spacing['spacing.general.x1'],
              paddingVertical: 5,
              paddingHorizontal: 10,
              backgroundColor: theme.colors[loanStatusColorTokens.background],
            }}
          >
            <Text color={loanStatusColorTokens.foreground} variant="bodyBase">
              {t(`card.status.${getManagementStatusKey(state)}`, { ns: 'loans' })}
            </Text>
          </View>
        </Row>
      ) : null}
      {showBody ? (
        <Text variant="bodyBase">
          {t(`${tPrefix}.body.${loanETA}.${isLender ? 'lender' : 'borrower'}`, {
            name: otherUser.profile!.firstName,
          })}
        </Text>
      ) : null}
      {showCTA ? (
        <Button disabled={disableCTA} marginTop="spacing.general.x4" onPress={onConfirmReturn} variant="primary">
          {t(getManagementCtaKey(state))}
        </Button>
      ) : null}
    </Column>
  );
};

export const LoanManagement = ({ loan: originalLoan, loanETA }: ComponentProps) => {
  const { t } = useTranslation('one-channel');
  const { data: user } = useUser();

  const createOfferFromRequestModalProps = useCreateOfferFromRequestModal(originalLoan);
  const { setIsOpen: setIsCreateOfferModalOpen } = createOfferFromRequestModalProps;

  const loanReturnConfirmationProps = useLoanReturnConfirmation(originalLoan, () => setIsCreateOfferModalOpen(true));
  const { loan, setIsOpen: setIsReturnConfirmationModalOpen } = loanReturnConfirmationProps;

  if (loan.status === LoanStatus.Rejected) {
    return null;
  }
  const state = getLoanManagementState(loan, loanETA, user?.id);

  return (
    <>
      <LoanManagementCard onConfirmReturn={() => setIsReturnConfirmationModalOpen(true)} {...{ state, t }} />
      <LoanReturnConfirmation {...loanReturnConfirmationProps} />
      <CreateOfferFromRequestModal {...createOfferFromRequestModalProps} />
    </>
  );
};

const styles = StyleSheet.create({
  block: {
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
    padding: 16,
  },
});
